// CompliancePage.jsx
import React, { useState, useEffect, useMemo } from "react";
import T from '../../components/common/T';
import { useNavigate, useLocation } from "react-router-dom";
import ComplianceListView from "./ComplianceListView";
import ComplianceKanbanView from "./ComplianceKanbanView";
import { useCompliance } from "../../context/ComplianceContext";
import { useAuth } from "../../context/AuthContext";
import PageLayout, { PageHeader, StatsGrid, Toolbar } from "../../components/common/PageLayout";
import ViewSwitcher from "../../components/common/ViewSwitcher";
import { TopPrimaryButton } from "../../components/common/StandardButtons";
import {
  ShieldCheckIcon, ExclamationTriangleIcon,
  MagnifyingGlassIcon, CheckCircleIcon
} from "@heroicons/react/24/outline";

const CompliancePage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { compliances, deleteCompliance, fetchComplianceById } = useCompliance();
  const { checkAccess } = useAuth();

  const [view, setView] = useState("list");
  const [statusFilter, setStatusFilter] = useState("All");

  // Deep-link sync
  useEffect(() => {
    const handleDeepLink = async () => {
      const queryParams = new URLSearchParams(location.search);
      const recordId = queryParams.get('id');
      if (recordId) {
        await fetchComplianceById(recordId);
        navigate(`/admin/compliance-safety/edit/${recordId}`, { replace: true });
      }
    };
    handleDeepLink();
  }, [location.search, fetchComplianceById, navigate]);

  const [searchTerm, setSearchTerm] = useState("");

  const filteredData = useMemo(() => {
    let result = compliances || [];
    if (statusFilter !== "All") {
      result = result.filter(item => item.status === statusFilter);
    }
    if (searchTerm) {
      const q = searchTerm.toLowerCase();
      result = result.filter(item =>
        item.title?.toLowerCase().includes(q) ||
        item.type?.toLowerCase().includes(q) ||
        item.status?.toLowerCase().includes(q) ||
        item.notes?.toLowerCase().includes(q) ||
        item.projectName?.toLowerCase().includes(q)
      );
    }
    return result;
  }, [compliances, statusFilter, searchTerm]);

  // ── Stats from ALL compliances ──────────────────────────────────────────
  const stats = useMemo(() => [
    {
      label: "Total Records",
      value: compliances.length,
      icon: ShieldCheckIcon,
      variant: "default",
      isActive: statusFilter === "All" || !statusFilter,
      onClick: () => setStatusFilter("All"),
    },
    {
      label: "Reported",
      value: compliances.filter(c => c.status === "Reported").length,
      icon: ExclamationTriangleIcon,
      variant: "orange",
      isActive: statusFilter === "Reported",
      onClick: () => setStatusFilter(s => s === "Reported" ? "All" : "Reported"),
    },
    {
      label: "Investigating",
      value: compliances.filter(c => c.status === "Under Investigation").length,
      icon: MagnifyingGlassIcon,
      variant: "blue",
      isActive: statusFilter === "Under Investigation",
      onClick: () => setStatusFilter(s => s === "Under Investigation" ? "All" : "Under Investigation"),
    },
    {
      label: "Resolved",
      value: compliances.filter(c => c.status === "Resolved").length,
      icon: CheckCircleIcon,
      variant: "green",
      isActive: statusFilter === "Resolved",
      onClick: () => setStatusFilter(s => s === "Resolved" ? "All" : "Resolved"),
    },
  ], [compliances, statusFilter]);

  return (
    <PageLayout>
      {/* Header */}
      <PageHeader
        icon={ShieldCheckIcon}
        iconColor="text-[#E8612C]"
        title="Compliance & Safety"
        subtitle="Manage safety permits, licenses, and incidents"
        onRefresh={() => window.location.reload()}
      >
        {checkAccess('compliance.create') && (
          <TopPrimaryButton
            onClick={() => navigate("/admin/compliance-safety/add")}
            label="Add Record"
          />
        )}
      </PageHeader>

      {/* Stats — clicking a card sets the status filter */}
      <StatsGrid stats={stats} cols={4} />

      {/* Standard Toolbar */}
      <Toolbar
        searchTerm={searchTerm}
        onSearch={setSearchTerm}
        searchPlaceholder="Search permits, incidents..."
        tabs={['All', 'Reported', 'Under Investigation', 'Resolved']}
        activeTab={statusFilter}
        onTabChange={setStatusFilter}
      >
        <ViewSwitcher view={view} onViewChange={setView} availableViews={['list', 'kanban']} />
      </Toolbar>

      {/* Content */}
      <div className="flex-1">
        {view === "list" ? (
          <ComplianceListView
            data={filteredData}
            onEdit={id => navigate(`/admin/compliance-safety/edit/${id}`)}
            onDelete={deleteCompliance}
          />
        ) : (
          <ComplianceKanbanView
            data={filteredData}
            onEdit={id => navigate(`/admin/compliance-safety/edit/${id}`)}
            onDelete={deleteCompliance}
          />
        )}
      </div>
    </PageLayout>
  );
};

export default CompliancePage;